"use client"

import { cn } from "@repo/ui/lib/utils"
import { Check, ChevronDown, Search, X, XCircle } from "lucide-react"
import * as React from "react"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { ScrollArea } from "../ui/scroll-area"
import { Separator } from "../ui/separator"

interface SelectOption<T = any> {
  value: T
  label: string
  description?: string
  disabled?: boolean
  group?: string
}

interface AdvancedSelectProps<T = any> {
  options: SelectOption<T>[]
  value?: T | T[]
  onValueChange?: (value: T | T[]) => void
  placeholder?: string
  searchPlaceholder?: string
  multiple?: boolean
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  className?: string
  maxSelected?: number
  onSearch?: (searchTerm: string) => void
  loading?: boolean
  emptyMessage?: string
  maxHeight?: string
  groupBy?: boolean
  renderOption?: (option: SelectOption<T>) => React.ReactNode
  renderSelectedValue?: (value: T | T[]) => React.ReactNode
}

const AdvancedSelect = React.forwardRef<
  HTMLDivElement,
  AdvancedSelectProps
>(({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search options...",
  multiple = false,
  searchable = true,
  clearable = true,
  disabled = false,
  className,
  maxSelected,
  onSearch,
  loading = false,
  emptyMessage = "No options found",
  maxHeight = "300px",
  groupBy = false,
  renderOption,
  renderSelectedValue,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState("")
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Handle search
  const handleSearch = React.useCallback((term: string) => {
    setSearchTerm(term)
    if (onSearch) {
      onSearch(term)
    }
  }, [onSearch])

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchable || !searchTerm.trim()) {
      return options
    }

    if (onSearch) {
      // If external search is provided, return options as-is
      return options
    }

    // Local filtering
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [options, searchTerm, searchable, onSearch])

  // Group options if needed
  const groupedOptions = React.useMemo(() => {
    if (!groupBy) {
      return { '': filteredOptions }
    }

    return filteredOptions.reduce((groups, option) => {
      const group = option.group || 'Other'
      if (!groups[group]) {
        groups[group] = []
      }
      groups[group].push(option)
      return groups
    }, {} as Record<string, SelectOption[]>)
  }, [filteredOptions, groupBy])

  // Get selected values as array
  const selectedValues = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : []
    }
    return value !== undefined ? [value] : []
  }, [value, multiple])

  // Check if option is selected
  const isSelected = React.useCallback((optionValue: any) => {
    return selectedValues.includes(optionValue)
  }, [selectedValues])

  // Handle option selection
  const handleSelect = React.useCallback((optionValue: any) => {
    if (disabled) return

    let newValue: any

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : []
      if (isSelected(optionValue)) {
        newValue = currentValues.filter(v => v !== optionValue)
      } else {
        if (maxSelected && currentValues.length >= maxSelected) {
          return // Don't add if max reached
        }
        newValue = [...currentValues, optionValue]
      }
    } else {
      newValue = optionValue
      setOpen(false)
    }

    onValueChange?.(newValue)
  }, [disabled, multiple, value, isSelected, maxSelected, onValueChange])

  // Handle clear all
  const handleClear = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return

    const newValue = multiple ? [] : undefined
    onValueChange?.(newValue)
  }, [disabled, multiple, onValueChange])

  // Handle remove single item in multi-select
  const handleRemoveItem = React.useCallback((itemValue: any, e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled || !multiple) return

    const currentValues = Array.isArray(value) ? value : []
    const newValue = currentValues.filter(v => v !== itemValue)
    onValueChange?.(newValue)
  }, [disabled, multiple, value, onValueChange])

  // Keyboard navigation
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (disabled) return

    switch (e.key) {
      case 'Enter':
        e.preventDefault()
        if (open && highlightedIndex >= 0) {
          const flatOptions = Object.values(groupedOptions).flat()
          const option = flatOptions[highlightedIndex]
          if (option) {
            handleSelect(option.value)
          }
        } else {
          setOpen(!open)
        }
        break
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
        } else {
          const flatOptions = Object.values(groupedOptions).flat()
          setHighlightedIndex(prev =>
            prev < flatOptions.length - 1 ? prev + 1 : 0
          )
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open) {
          const flatOptions = Object.values(groupedOptions).flat()
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : flatOptions.length - 1
          )
        }
        break
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }, [disabled, open, highlightedIndex, groupedOptions, handleSelect])

  // Auto focus search input when opened
  React.useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [open, searchable])

  // Reset highlight when options change
  React.useEffect(() => {
    setHighlightedIndex(-1)
  }, [filteredOptions])

  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }

    if (open && typeof document !== 'undefined') {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  // Render selected value(s)
  const renderSelected = () => {
    if (renderSelectedValue) {
      return renderSelectedValue(value)
    }

    if (selectedValues.length === 0) {
      return <span className="text-muted-foreground font-normal">{placeholder}</span>
    }

    if (multiple) {
      if (selectedValues.length === 1) {
        const option = options.find(opt => opt.value === selectedValues[0])
        return <span className="font-medium text-foreground">{option?.label || selectedValues[0]}</span>
      }
      return (
        <span className="font-medium text-foreground">
          {selectedValues.length} item{selectedValues.length !== 1 ? 's' : ''} selected
        </span>
      )
    } else {
      const option = options.find(opt => opt.value === selectedValues[0])
      return <span className="font-medium text-foreground">{option?.label || selectedValues[0]}</span>
    }
  }

  // Render badges for multi-select
  const renderBadges = () => {
    if (!multiple || selectedValues.length === 0) return null

    return (
      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t">
        {selectedValues.map((val) => {
          const option = options.find(opt => opt.value === val)
          return (
            <Badge
              key={val}
              variant="secondary"
              className="text-xs px-2.5 py-1 bg-secondary/80 hover:bg-secondary text-secondary-foreground border border-border/50"
            >
              <span className="mr-1.5">{option?.label || val}</span>
              <button
                type="button"
                className="ml-0.5 hover:bg-secondary-foreground/20 rounded-full p-0.5 transition-colors"
                onClick={(e) => handleRemoveItem(val, e)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )
        })}
      </div>
    )
  }

  return (
    <div className={cn("relative", className)} ref={ref} {...props}>
      {/* Trigger */}
      <Button
        type="button"
        ref={triggerRef}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          "w-full justify-between text-left font-normal min-h-[40px] h-auto py-3 px-4",
          "border-2 hover:border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20",
          "transition-all duration-200",
          !selectedValues.length && "text-muted-foreground",
          disabled && "cursor-not-allowed opacity-50 hover:border-border",
          open && "border-primary ring-2 ring-primary/20"
        )}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        <div className="flex-1 overflow-hidden text-left">
          {renderSelected()}
        </div>
        <div className="flex items-center gap-2 ml-3">
          {clearable && selectedValues.length > 0 && !disabled && (
            <XCircle
              className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200",
            open && "rotate-180 text-primary"
          )} />
        </div>
      </Button>

      {/* Badges for multi-select */}
      {renderBadges()}

      {/* Dropdown Content */}
      {open && (
        <div
          ref={contentRef}
          className={cn(
            "relative mt-2 bg-background border-2 border-primary/20 rounded-lg shadow-lg",
            "animate-in fade-in-0 zoom-in-95 duration-200"
          )}
        >
          {/* Search Input */}
          {searchable && (
            <div className="p-4 pb-2 border-b border-border/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20"
                />
              </div>
            </div>
          )}

          {/* Options */}
          <ScrollArea
            className="rounded-b-lg"
            style={{ height: maxHeight }}
          >
            <div className="p-2">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent mr-3"></div>
                  Loading options...
                </div>
              ) : Object.keys(groupedOptions).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Search className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm font-medium">{emptyMessage}</p>
                  <p className="text-xs mt-1">Try adjusting your search terms</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {Object.entries(groupedOptions).map(([group, groupOptions], groupIndex) => (
                    <div key={group}>
                      {groupBy && group && (
                        <>
                          {groupIndex > 0 && <Separator className="my-3" />}
                          <div className="px-3 py-2 text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                            {group}
                          </div>
                        </>
                      )}
                      {groupOptions.map((option, optionIndex) => {
                        const flatIndex = Object.values(groupedOptions)
                          .slice(0, groupIndex)
                          .reduce((acc, opts) => acc + opts.length, 0) + optionIndex
                        const isHighlighted = flatIndex === highlightedIndex
                        const selected = isSelected(option.value)

                        return (
                          <div
                            key={option.value}
                            className={cn(
                              "flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer rounded-md",
                              "transition-colors duration-150",
                              "hover:bg-accent/80 hover:text-accent-foreground",
                              isHighlighted && "bg-accent text-accent-foreground",
                              option.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
                              selected && "bg-primary/10 text-primary hover:bg-primary/15"
                            )}
                            onClick={() => !option.disabled && handleSelect(option.value)}
                            onMouseEnter={() => !option.disabled && setHighlightedIndex(flatIndex)}
                          >
                            {multiple ? (
                              <Checkbox
                                checked={selected}
                                disabled={option.disabled}
                                className="pointer-events-none data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                              />
                            ) : (
                              <div className="w-4 h-4 flex items-center justify-center">
                                {selected && <Check className="h-4 w-4 text-primary font-bold" />}
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              {renderOption ? (
                                renderOption(option)
                              ) : (
                                <div>
                                  <div className={cn(
                                    "font-medium truncate",
                                    selected && "text-primary"
                                  )}>
                                    {option.label}
                                  </div>
                                  {option.description && (
                                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                      {option.description}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  )
})

AdvancedSelect.displayName = "AdvancedSelect"

export { AdvancedSelect }
export type { AdvancedSelectProps, SelectOption }

